import React, { useEffect, useState } from "react";
import styles from "./UserDisplay.module.scss";
import UserAvatar from "./UserAvatar";
import * as UserApi from "@src/api/UserApi";
import { useToast, Button } from "@swordsweeper/ssi-ui-component-library";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { Clinic } from "@src/types/clinic";
import Modal, { ModalSizes } from "@src/components/common/Modal";
import FormContainer from "@src/components/containers/FormContainer/FormContainer";
import ClinicSelectionForm, {
  schema as ClinicSelectionFormSchema,
} from "@src/components/forms/ClinicSelectionForm";

interface UserDisplayInfo {
  initials?: string;
  avatarImage?: string;
  avatarBackgroundColor?: string;
  activeClinic?: Clinic | null;
  fullName?: string;
}

interface UserDisplayProps {
  userId?: string;
  initialUserDisplayInfo?: UserDisplayInfo | null;
  isForCurrentUser?: boolean;
  avatarOnly?: boolean;
}

export default function UserDisplay({
  userId,
  avatarOnly = false,
  isForCurrentUser = false,
}: UserDisplayProps) {
  const { showToast } = useToast();
  const [showSwitchClinicsModal, setShowSwitchClinicsModal] = useState(false);
  const [isLoadingUserDisplayInfo, setIsLoadingUserDisplayInfo] =
    useState(false);
  const [userDisplayInfo, setUserDisplayInfo] =
    useState<UserDisplayInfo | null>(null);
  const { currentUser, setActiveClinic } = useCurrentUserStore();

  const userData = isForCurrentUser ? currentUser : userDisplayInfo;

  useEffect(() => {
    if (!!userId && !isForCurrentUser) {
      handleLoadUserDisplayInfo();
    }
  }, [userId, isForCurrentUser]);

  const handleLoadUserDisplayInfo = async () => {
    setIsLoadingUserDisplayInfo(true);
    try {
      const response = await UserApi.getUserDisplayInfo(userId);
      setUserDisplayInfo(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading user display info",
        variant: "error",
      });
    } finally {
      setIsLoadingUserDisplayInfo(false);
    }
  };

  const handleActiveClinicSetSuccessfully = () => {
    // Reload the page so any page data gets reloaded with new active clinic context
    window.location.reload();
  };

  return (
    <div className={styles.root}>
      <div className="flex-none">
        <UserAvatar
          userInitials={userData?.initials}
          userAvatarImageSrc={userData?.avatarImage}
          backgroundColor={userData?.avatarBackgroundColor}
        />
      </div>
      {!avatarOnly && (
        <div className={styles.userInfo}>
          <div>{userData?.fullName}</div>
          <div className="flex-row flex-center flex-start spaced-content">
            <div>{userData?.activeClinic?.name || "Unidentified Clinic"}</div>

            {isForCurrentUser && userData.clinicUsers?.length > 1 && (
              <>
                <span>|</span>
                <Button
                  className="btn-link"
                  onClick={() => setShowSwitchClinicsModal(true)}
                >
                  Switch Clinics
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        isShowing={showSwitchClinicsModal}
        size={ModalSizes.Small}
        onClose={() => setShowSwitchClinicsModal(false)}
        header="Who are you shopping for?"
      >
        <FormContainer
          form={ClinicSelectionForm}
          schema={ClinicSelectionFormSchema}
          initialData={{ clinicId: userData?.activeClinic?.id }}
          additionalFormProps={{
            clinicUsers: userData.clinicUsers,
          }}
          onSubmit={({ clinicId }) =>
            setActiveClinic(clinicId, handleActiveClinicSetSuccessfully)
          }
          submitButtonText={
            <span className="text-lg">Switch to This Clinic</span>
          }
          submitButtonClassName="width-100"
        />
      </Modal>
    </div>
  );
}
